<script lang="ts" setup>
import { Overlay, showSuccessToast, Swipe, SwipeItem } from "vant";
import MagicStickIcon from "@/components/icon/MagicStickIcon.vue";
import CopyIcon from "@/components/icon/copyIcon.vue";
import { computed } from "vue";
import { copyToClipboard, getDisplayDateTime, trim } from "@/modules/common";
import { usePrompts } from "@/stores/prompts";
import IShapeIcon from "@/components/icon/iShapeIcon.vue";
import { useTask } from "@/stores/task";
import { useI18n } from "vue-i18n";
import { storeToRefs } from "pinia";
import { useRawImageUrl } from "@/use/common";

const taskStore = useTask();
const { t } = useI18n();

const { activeTaskId } = storeToRefs(taskStore);

const task = computed(() => taskStore.getTask(activeTaskId.value));
const entity = computed(
  () => taskStore.getMedia(activeTaskId.value) as TextImage
);
// 提示词
const promptStore = usePrompts();

const prompt = computed(() => {
  let { prompt, mateTrait } = entity.value as TextImage;
  if (!prompt || task.value?.tab === "TextImage") prompt = "";
  prompt = prompt.trim();
  if (prompt.length > 0 && prompt[prompt.length - 1] !== ",") prompt += ",";
  prompt +=
    promptStore.getTextByTextImage(entity.value as TextImage).positive || "";
  if (mateTrait && task.value?.tab === "TextImage") {
    prompt += mateTrait!.prompt || "";
    if (prompt.length > 0 && prompt[prompt.length - 1] !== ",") prompt += ",";
  }
  return trim(prompt, ",");
});
const tags = computed(() => {
  return promptStore
    .getPromptListByText(prompt.value)
    .map((item) => item.displayText + (item.weight ? `:${item.weight}` : ""));
});
// Event Handler
const onCopy = async () => {
  copyToClipboard(prompt.value);
  showSuccessToast(t("Message.copySuccessful"));
};
</script>
<template>
  <overlay
    :lock-scroll="false"
    :show="taskStore.showTaskDetailModal"
    class="z-30 backdrop-blur van-safe-area-top"
    @click="taskStore.showTaskDetailModal = false"
  >
    <template v-if="taskStore.showTaskDetailModal">
      <div
        class="fixed left-3 z-10 bg-base-200/30 p-2 rounded-full text-white top-[calc(env(safe-area-inset-top)+1rem)]"
        @click="taskStore.showTaskDetailModal = false"
      >
        <i class="iconfont icon-left"></i>
      </div>
      <div class="w-full h-full flex px-4 items-center">
        <div
          :class="[
            'w-full max-w-[500px] my-4 mx-auto flex justify-center max-h-full  h-fit',
            !taskStore.getMedia(activeTaskId)?.media && 'items-center',
          ]"
        >
          <div
            class="rounded-xl bg-base-100 shadow-lg overflow-auto w-full"
            @click.stop
          >
            <video
              v-if="task?.tab === 'StoryVideo'"
              :poster="entity?.media?.videoCoverUrl"
              :src="entity?.media?.url"
              class="w-full object-contain"
              controls
              crossorigin="anonymous"
            />
            <swipe
              v-else-if="entity?.media && (entity as TextImage)?.source"
              class="w-full"
              indicator-color="white"
            >
              <swipe-item class="flex">
                <img
                  :src="entity.media.url.replace('/thumb', '/public')"
                  alt=""
                  class="w-full object-contain"
                />
              </swipe-item>
              <swipe-item class="relative h-full">
                <img
                  :src="(entity as TextImage)?.source?.url"
                  alt=""
                  class="w-full object-contain"
                />
                <div
                  class="absolute bottom-2 right-2 p-1 bg-base-100/40 rounded text-sm"
                >
                  <div class="text-white/80">
                    {{ $t("Image.originalImage") }}
                  </div>
                </div>
              </swipe-item>
            </swipe>
            <BaseImg
              v-else-if="entity?.media"
              :key="entity.media.url"
              :entity="entity"
              :isShowNsfw="false"
              :media="entity.media"
              :url="useRawImageUrl(entity.media.url)"
              class="w-full"
              fit="cover"
              original
            />
            <div v-else class="w-full"></div>
            <!--            <button @click="downloadImage(entity.media!.url)">-->
            <!--              <download-icon class="icon-base" />-->
            <!--            </button>-->
            <div class="p-4 flex flex-col gap-4">
              <div class="flex justify-between items-center">
                <div>#ID {{ activeTaskId }}</div>
              </div>
              <div class="flex flex-col bg-base-200 rounded-lg">
                <div class="flex justify-between items-center border-b 0 p-3">
                  <span class="flex gap-1 items-center">
                    <magic-stick-icon class="icon-sm" />
                    {{ $t("Texts.promptText") }}
                  </span>
                  <div v-if="prompt">
                    <button
                      class="btn btn-ghost btn-xs text-base-content/50"
                      @click="onCopy"
                    >
                      <copy-icon class="icon-sm" />
                      {{ $t("Buttons.copy") }}
                    </button>
                  </div>
                </div>
                <div class="flex flex-col p-3 gap-2">
                  <div class="flex gap-1 flex-wrap">
                    <div v-for="(tag, idx) in tags" :key="idx" class="badge">
                      {{ tag }}
                    </div>
                  </div>
                  <div class="text-base-content/50 break-words">
                    {{ prompt ? prompt : $t("Message.noPromptText") }}
                  </div>
                </div>
              </div>
              <div class="flex flex-col bg-base-200 rounded-lg">
                <div class="flex justify-between items-center border-b 0 p-3">
                  <span class="flex gap-1 items-center">
                    <i-shape-icon class="fill-current" />
                    {{ $t("Image.info") }}
                  </span>
                </div>
                <div class="flex justify-between text-xs p-3">
                  <div v-if="entity?.media?.width && entity.media.height">
                    <div class="text-base-content/50">
                      {{ $t("Image.size") }}
                    </div>
                    <div>
                      {{ `${entity.media.width}x${entity.media.height}` }}
                    </div>
                  </div>
                  <div>
                    <div class="text-base-content/50">
                      {{ $t("Image.date") }}
                    </div>
                    <div>{{ getDisplayDateTime(task?.dtCreate!) }}</div>
                  </div>
                  <div>
                    <div class="text-base-content/50">
                      {{ $t("Image.type") }}
                    </div>
                    <div>{{ t(`task.tab.${task?.tab}`) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </overlay>
</template>

<style scoped>
:deep(.van-swipe__track) {
  display: flex;
  align-items: center;
}
</style>
